<template>
    <div class="uik-upload">
        <div
            v-if="isType('pond') || isType('avatar') || isType('cover')"
            class="uik-upload__pond"
            :class="{'uik-upload__pond--avatar': isType('avatar')}"
        >
            <UikContentTitle v-if="label && (isType('pond') || isType('cover'))">{{ label }}</UikContentTitle>
            <div
                class="uik-upload__pond-wrapper"
                :class="{
                    'uik-upload__pond-wrapper--pond': isType('pond'),
                    'uik-upload__pond-wrapper--cover': isType('cover'),
                    'uik-upload__pond-wrapper--avatar': isType('avatar')
                }"
            >
                <label
                    ref="pond"
                    :for="labelFor"
                    class="uik-upload__pond-area"
                    :class="[
                        {'uik-upload__pond-area--file': value || showImageFromUrl},
                        'uik-upload__pond-area--' + status
                    ]"
                    :style="isType('avatar') || isType('cover') ? ('background-image: url(' + imageUrl + ');') : ''"
                    @click="downloadFile()"
                >
                    <div v-if="!value && getIcon && !showImageFromUrl" class="uik-upload__pond-icons">
                        <div v-for="i in 3" :key="i" class="uik-upload__pond-icon">
                            <UikIcon :icon="['fad', getIcon]" />
                        </div>
                    </div>
                    <div v-if="!value && isType('pond')" class="uik-upload__text" v-html="text.pond" />
                    <div v-if="value && isType('pond')" class="uik-upload__file">
                        <div
                            v-if="fileIcon || imageUrl"
                            class="uik-upload__file-icon"
                            :class="{'uik-upload__file-icon--image': imageUrl}"
                        >
                            <UikIcon v-if="!imageUrl" :icon="['fad', fileIcon]" />
                            <div
                                v-if="imageUrl"
                                :style="'background-image: url(' + imageUrl + ');'"
                                class="uik-upload__file-icon-image"
                            />
                        </div>
                        <div class="uik-upload__file-info">
                            <span class="uik-upload__file-name">{{ isFile ? value.name : value.file_name }}</span>
                            <span v-if="status" class="uik-upload__status">
                                <span
                                    v-if="status !== 'uploading'"
                                    class="uik-upload__status-text"
                                    :class="'uik-upload__status-text--' + status"
                                >{{ text.status[status] }}</span>
                                <div v-if="status === 'uploading'" class="uik-upload__progress">
                                    <UikProgressBar :fill="String(getProgress)" color="primary" />
                                </div>
                            </span>
                        </div>
                    </div>
                    <div
                        v-if="value && !getError && getProgress !== 100"
                        :style="(isType('avatar') ? 'height' : 'width') + ': ' + (isType('avatar') || isType('cover') ? (100 - getProgress) : getProgress) + '%;'"
                        class="uik-upload__backdrop-progress"
                    />
                    <div
                        v-if="(isType('avatar') || isType('cover')) && (value || showImageFromUrl) && status !== 'uploading'"
                        class="uik-upload__image-overlay"
                    >
                        <UikIcon :icon="['fad', getIcon]" />
                        <span>{{ text.change }}</span>
                    </div>
                </label>
                <button
                    v-if="(value || showImageFromUrl) && status !== 'uploading'"
                    class="uik-upload__remove-file"
                    :title="text.remove"
                    @click="removeFile()"
                >
                    <UikIcon :icon="['far', 'times']" />
                </button>
            </div>
        </div>
        <label
            v-if="isType('overlay')"
            ref="overlay"
            class="uik-upload__overlay"
            :for="status !== 'uploading' ? _uid : ''"
        >
            <div class="uik-upload__overlay-content">
                <div v-if="getIcon" class="uik-upload__overlay-icons">
                    <div v-for="i in 3" :key="i" class="uik-upload__overlay-icon">
                        <UikIcon :icon="['fad', getIcon]" />
                    </div>
                </div>
                <div class="uik-upload__overlay-text">{{ text.overlay }}</div>
            </div>
        </label>
        <input
            :id="_uid"
            ref="input"
            :accept="accept"
            class="uik-upload__input"
            type="file"
            @input="processFile($event)"
        />
    </div>
</template>

<script>
export default {
    name: "UikUpload",
    model: {
        prop: "value",
        event: "change"
    },
    props: {
        /**
         * Current file / value
         *
         * Object data example: { file_name: "Avatar.jpg", mime_type: "image/jpeg" }
         */
        value: { type: [Object, File], default: null },
        /**
         * Displayed label (visible only with type "pond")
         */
        label: { type: String, default: "" },
        /**
         * Override auto-generated icon
         */
        icon: { type: String, default: "" },
        /**
         * Accept attribute for input element (accepts same value as native attribute)
         * Initial icons are generated based on first found parameter
         */
        accept: { type: String, default: "" },
        /**
         * Type(s) of component
         *
         * Available types: "pond" (default), "avatar", "cover", "overlay"
         * Can use multiple types - e.g. ["pond", "overlay"]
         */
        type: { type: [String, Array], default: "pond" },
        /**
         * Starts upload with initial value on mounted (if value exists)
         */
        initUpload: { type: Boolean, default: false },
        /**
         * Specifies file's URL
         *
         * Can be used to:
         * - display existing avatar or cover
         * - enable "click to open file" functionality in pond (also requires media object as value to work)
         */
        fileUrl: { type: String, default: "" },
        /**
         * Component's locale
         *
         * @todo - Add proper UI-Kit locale implementation
         */
        text: {
            type: Object,
            default: () => {
                return {
                    pond: "<b>Choose file</b> or drag it in here",
                    overlay: "Drop file to upload",
                    remove: "Remove file",
                    change: "Change",
                    status: {
                        uploading: "Uploading",
                        completed: "Completed",
                        error: "Error"
                    }
                }
            }
        },
        /**
         * Disable default Media implementation
         */
        customImplementation: { type: Boolean, default: false },
        /**
         * Sets error
         */
        error: { type: Boolean, default: false },
        /**
         * Sets progress
         */
        progress: { type: Number, default: null },
        /**
         * Disables automatic uploading when file is processed
         */
        disableAutoUpload: { type: Boolean, default: false },
        /**
         * Parameters sent in store request
         */
        params: { type: Object, default () { return {} } }
    },
    data () {
        return {
            /**
             * Was there error at upload (Default Media implementation)
             */
            isError: false,
            /**
             * Temporary image to keep preview if value changes to media object
             */
            tempImage: null,
            /**
             * Active file url, based on "fileUrl" prop
             */
            activeFileUrl: null
        }
    },
    computed: {
        /**
       * Value for "for" attribute on input element
       *
       * @return { String } component's uid, except when "click to open file" functionality is expected
       */
        labelFor () {
            if (
                this.value &&
            !this.isFile &&
            !this.isType("avatar") &&
            !this.isType("cover") &&
            this.status !== "error" &&
            this.status !== "uploading" &&
            !!this.activeFileUrl
            ) {
                this.$emit("uid", "")
                return ""
            }

            this.$emit("uid", this._uid)
            return this._uid
        },
        /**
         * Check if current value is JavaScript file object
         *
         * @return { Boolean } is value File
         */
        isFile () {
            if (this.value && this.value instanceof File) return true
            return false
        },
        /**
         * Gets url of image to display
         * Creates "tempImage"
         *
         * @return { String } image url
         */
        imageUrl () {
            if (this.value && this.isFile && this.value.type.startsWith("image")) {
                const url = URL.createObjectURL(this.value)
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.tempImage = url
                return url
            }

            if (this.tempImage) return this.tempImage

            if (this.showImageFromUrl) return this.activeFileUrl

            return null
        },
        /**
         * Checks if image can/should be displayed from URL
         *
         * @return { Boolean } is image displayed from URL
         */
        showImageFromUrl () {
            return !!this.activeFileUrl && (
                (this.isType("avatar") || this.isType("cover")) ||
                (this.isType("pond") && this.value && this.value.mime_type && this.value.mime_type.startsWith("image"))
            )
        },
        /**
         * Generates default icon based on type, icon and accept props
         *
         * @return { String } icon name
         */
        getIcon () {
            if (this.isType("avatar")) return "camera"
            if (this.isType("cover")) return "image"

            if (this.icon) return this.icon

            if (this.accept) {
                let icon = this.accept.split(",")[0]
                if (this.$options.icons[icon]) { return "file-" + this.$options.icons[icon] }
            }
            return "file"
        },
        /**
         * Generates icon based on file extension or returns the default
         *
         * @return { String } icon name
         */
        fileIcon () {
            if (this.icon) return this.icon
            let type = this.isFile ? this.value.name.split(".") : this.value.file_name.split(".")

            let icon = this.$options.icons["." + type[type.length - 1].toLowerCase()]
            if (icon) return "file-" + icon
            return this.getIcon
        },
        /**
         * Generates status based on progress
         *
         * @return { String } status
         */
        status () {
            if (this.getError) return "error"
            if (this.value) {
                let progress = this.getProgress
                if (progress === 100) return "completed"
                if (progress > 0 && progress < 100) return "uploading"
            }
            return ""
        },
        /**
         * Gets progress
         *
         * @return { Number }
         */
        getProgress () {
            return this.useDefaultImplementation ? this.$api.Media.state.progress : this.progress
        },
        /**
         * Checks if error is present
         *
         * @return { Boolean }
         */
        getError () {
            return this.error || this.isError
        },
        /**
         * Checks if default Media implementation is to be used
         *
         * @return { Boolean }
         */
        useDefaultImplementation () {
            return !!this.$api && !!this.$api.Media && !this.customImplementation
        }
    },
    watch: {
        /**
       * Removes temporary image if value is changed (file removed) outside the component
       */
        value () {
            if (this.tempImage && !this.value) this.tempImage = null
        },
        /**
         * Updates "activeFileUrl"
         */
        fileUrl () {
            this.setFileUrl()
        }
    },
    mounted () {
        this.$nextTick(() => {
            this.setDropAreas()
            if (this.initUpload && this.value) this.processFile(this.value)
            this.setFileUrl()
        })
    },
    beforeDestroy () {
        this.removeDropAreas()
    },
    methods: {
        /**
         * Processes file when it's added
         *
         * @param { Object } event event when file is added
         */
        processFile (event) {
            let file = event instanceof File ? event : (event.target.files ? event.target.files[0] : event.dataTransfer.files[0])
            this.$emit("processing", file)
            if (file && this.validateFileType(file)) {
                this.$emit("change", file)
                this.isError = false
                if (this.tempImage && (!this.value || !this.value.type || !this.value.type.startsWith("image"))) this.tempImage = null
                if (this.activeFileUrl) this.activeFileUrl = null
                if (this.type !== "overlay" && !this.disableAutoUpload) {
                    this.$emit("start-upload", file)
                    if (this.useDefaultImplementation) this.$nextTick(() => { this.startUpload() })
                }
            }
        },
        /**
         * Uploads file with default Media implementation
         */
        async startUpload () {
            let file = this.value
            try {
                let res = await this.$api.Media.store({ file, ...this.params })
                this.$emit("change", res.data)
                this.$emit("completed", res.data)
            } catch (error) {
                this.isError = true
                this.$emit("error")
            }
        },
        /**
         * Removes file and resets corresponding values
         */
        removeFile () {
            this.$emit("remove", this.value)
            this.$emit("change", null)
            this.isError = false
            this.tempImage = null
            this.activeFileUrl = null
            this.$refs.input.value = ""
        },
        /**
         * Sets "activeFileUrl" data from "fileUrl" prop
         */
        setFileUrl () {
            this.activeFileUrl = this.fileUrl
        },
        /**
         * Creates file drop areas
         */
        setDropAreas () {
            if (
                this.isType("pond") ||
                this.isType("avatar") ||
                this.isType("cover")
            ) {
                let area = this.$refs.pond;

                ["dragenter", "dragover"].forEach((eventName) => {
                    area["on" + eventName] = () => {
                        this.setDropClass(area)
                        this.preventDefaults(event)
                    }
                })
                area.ondragleave = () => {
                    this.removeDropClass(area)
                    this.preventDefaults(event)
                }
                area.ondrop = () => {
                    this.dropFile(event)
                    this.removeDropClass(area)
                    this.preventDefaults(event)
                }
            }

            if (this.isType("overlay")) {
                let area = document.getElementById("app")
                let target = this.$refs.overlay;

                ["dragenter", "dragover"].forEach((eventName) => {
                    area["on" + eventName] = () => {
                        this.setDropClass(target)
                        this.preventDefaults(event)
                    }
                })
                area.ondragleave = () => {
                    this.removeDropClass(target)
                    this.preventDefaults(event)
                }
                area.ondrop = () => {
                    this.dropFile(event)
                    this.removeDropClass(target)
                    this.preventDefaults(event)
                }
            }
        },
        /**
         * Removes file drop areas
         */
        removeDropAreas () {
            let area

            if (this.isType("pond") || this.isType("avatar") || this.isType("cover")) { area = this.$refs.pond }
            if (this.isType("overlay")) area = document.getElementById("app");
            ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
                area["on" + eventName] = null
            })
        },
        /**
         * Prevents event's default behavior
         *
         * @param { Object } e event
         */
        preventDefaults (e) {
            e.preventDefault()
            e.stopPropagation()
        },
        /**
         * Sets file drop class on DOM element
         *
         * @param { Object } el DOM element
         */
        setDropClass (el) {
            let className = "uik-upload__drop"
            if (!el.classList.contains(className)) el.classList.add(className)
        },
        /**
         * Removes file drop class on DOM element
         *
         * @param { Object } el DOM element
         */
        removeDropClass (el) {
            let className = "uik-upload__drop"
            if (el.classList.contains(className)) el.classList.remove(className)
        },
        /**
         * Passes file through validation onto processing when it's dropped
         *
         * @param { Object } event event when file is droppped
         */
        dropFile (event) {
            if (this.validateFileType(event.dataTransfer.files[0])) { this.processFile(event) }
        },
        /**
         * Validates file type based on "accept" value
         *
         * @param { Object } file
         *
         * @return { Boolean }
         */
        validateFileType (file) {
            if (!this.accept) {
                this.$emit("validate", true)
                return true
            }

            try {
                let fileName = file.name.split(".")
                let fileFormat = fileName[fileName.length - 1].toLowerCase()
                let fileType = file.type.split("/")[0]

                let validTypes = this.accept.split(",")
                for (let i = 0; i < validTypes.length; i++) {
                    let type = validTypes[i]
                        .replace("/*", "")
                        .replace(".", "")
                        .trim()
                        .toLowerCase()
                    if (type === fileFormat || type === fileType) {
                        this.$emit("validate", true)
                        return true
                    }
                }
            } catch {
                this.$emit("validate", null)
                return false
            }

            this.$emit("validate", false)
            return false
        },
        /**
         * Checks if component type is active
         *
         * @param { String } type type to check
         *
         * @return { Boolean }
         */
        isType (type) {
            return this.type === type || this.type.includes(type)
        },
        /**
         * Opens file URL in a new window
         */
        downloadFile () {
            if (this.labelFor === "") {
                window.open(this.activeFileUrl, "_blank")
            }
        }
    },
    icons: {
        ".doc": "word",
        ".docx": "word",
        ".docm": "word",
        ".txt": "alt",
        "video/*": "video",
        ".mkv": "video",
        ".ogm": "video",
        ".flv": "video",
        ".amv": "video",
        ".ogv": "video",
        ".ogg": "video",
        ".mpg": "video",
        ".mpeg": "video",
        ".m2v": "video",
        ".avi": "video",
        ".mov": "video",
        ".wmv": "video",
        ".mp4": "video",
        ".m4v": "video",
        ".3gp": "video",
        ".3g2": "video",
        ".webm": "video",
        ".asx": "video",
        ".ppt": "powerpoint",
        ".ppsx": "powerpoint",
        ".pptm": "powerpoint",
        ".pptx": "powerpoint",
        ".pdf": "pdf",
        "audio/*": "music",
        ".mp3": "music",
        ".opus": "music",
        ".weba": "music",
        ".wav": "music",
        ".oga": "music",
        ".mid": "music",
        ".aiff": "music",
        ".au": "music",
        ".aac": "music",
        ".amr": "music",
        ".flac": "music",
        ".wma": "music",
        "image/*": "image",
        ".jpg": "image",
        ".jpeg": "image",
        ".pjpeg": "image",
        ".tiff": "image",
        ".tif": "image",
        ".gif": "image",
        ".bmp": "image",
        ".xbm": "image",
        ".pjp": "image",
        ".png": "image",
        ".apng": "image",
        ".webp": "image",
        ".svg": "image",
        ".svgz": "image",
        ".ico": "image",
        ".jfif": "image",
        ".xlsx": "excel",
        ".xlsm": "excel",
        ".xlsb": "excel",
        ".xltx": "excel",
        ".xml": "code",
        ".json": "code",
        ".html": "code",
        ".css": "code",
        ".scss": "code",
        ".js": "code",
        ".vue": "code",
        ".php": "code",
        ".rar": "archive",
        ".zip": "archive",
        ".gzip": "archive",
        ".gz": "archive",
        ".iso": "archive",
        ".csv": "csv",
        ".scsv": "csv"
    }
}
</script>

<style lang="scss">
.uik-upload {
  .uik-upload__input {
    display: none;
  }
  .uik-upload__pond {
    width: 100%;
    .uik-upload__pond-area {
      display: flex;
      flex-flow: column nowrap;
      justify-content: center;
      align-items: center;
      width: 100%;
      overflow: hidden;
      border-radius: $borderRadius;
      background: rgba($borderColor, 0.35);
      border: dashed 2px rgba($borderColorDarker, 0.8);
      height: 140px;
      padding: 20px;
      transition: all 0.18s, height 0.25s ease-out, border-style 0s,
        border-width 0s;
      user-select: none;
      .uik-upload__pond-icons {
        position: relative;
        display: flex;
        flex-flow: row nowrap;
        justify-content: center;
        align-items: center;
        pointer-events: none;
        .uik-upload__pond-icon {
          background: $bg;
          font-size: 44px;
          color: desaturate($primary, 70%);
          display: flex;
          flex-flow: row nowrap;
          justify-content: center;
          align-items: center;
          transition: all 0.18s;
          position: relative;
          z-index: 2;
          opacity: 0.7;
          svg {
            --fa-primary-opacity: 0.95;
            --fa-secondary-opacity: 0.25;
          }
          &:first-child,
          &:last-child {
            background: transparent;
            position: absolute;
            opacity: 0;
            z-index: 1;
          }
        }
        & + .uik-upload__text {
          margin-top: 13px;
        }
      }
      .uik-upload__text {
        font-size: 14px;
        line-height: 19px;
        transition: all 0.18s;
        font-weight: 400;
        color: $textSecondary;
        text-align: center;
        b {
          color: $primary;
          font-weight: 500;
          &:hover {
            text-decoration: underline;
          }
        }
      }
      &:hover,
      &.uik-upload__drop {
        cursor: pointer;
        background: rgba($primary, 0.05);
        border-color: rgba($primary, 0.75);
        .uik-upload__pond-icon {
          opacity: 1;
          transform: scale(1.2) translateY(-3px);
          color: desaturate($primary, 50%);
          &:nth-child(2) {
            color: desaturate($primary, 45%);
            svg {
              --fa-primary-color: #{$primary};
            }
          }
          &:first-child,
          &:last-child {
            opacity: 0.5;
          }
          &:first-child {
            transform: scale(0.9) translateY(-3px) translateX(-20px)
              rotate(-15deg);
          }
          &:last-child {
            transform: scale(0.9) translateY(-3px) translateX(20px)
              rotate(15deg);
          }
        }
        & + .uik-upload__remove-file {
          background: darken($borderColor, 1%);
          svg {
            color: rgba($textMain, 0.35);
          }
        }
      }
      &:active {
        border-color: rgba($primary, 0.75);
        background: rgba($primary, 0.075);
      }
      &--file {
        border: $borderDefault;
        background: rgba($borderColor, 0.25);
        height: 76px;
        padding: 19px 20px;
        box-shadow: $boxShadow;
        position: relative;
        transition: all 0.18s, height 0.2s ease-out;
        animation: uik-upload-file 0.5s 0.1s backwards;
        @keyframes uik-upload-file {
          from {
            background: rgba($borderColor, 0.25);
            border: $borderDefault;
            opacity: 0;
            transform: scale(0.95);
          }
          40% {
            background: rgba($borderColor, 0.25);
            border: $borderDefault;
            opacity: 1;
            transform: none;
          }
        }
        .uik-upload__file {
          width: 100%;
          display: flex;
          flex-flow: row nowrap;
          justify-content: flex-start;
          align-items: center;
          position: relative;
          z-index: 1;
          .uik-upload__file-icon {
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 36px;
            height: 36px;
            width: 27px;
            border-radius: $borderRadius;
            color: desaturate($primary, 65%);
            svg {
              --fa-primary-opacity: 0.95;
              --fa-secondary-opacity: 0.25;
            }
            .uik-upload__file-icon-image {
              background-position: center;
              background-repeat: no-repeat;
              background-size: cover;
              height: 100%;
              width: 100%;
              border-radius: $borderRadius;
              box-shadow: $boxShadow;
            }
            & + .uik-upload__file-info {
              padding-left: 15px;
            }
            &--image {
              width: 36px;
              & + .uik-upload__file-info {
                max-width: calc(100% - 77px);
              }
            }
          }
          .uik-upload__file-info {
            flex-grow: 1;
            display: flex;
            flex-flow: column nowrap;
            justify-content: center;
            align-items: flex-start;
            white-space: nowrap;
            max-width: calc(100% - 68px);
            .uik-upload__file-name {
              font-size: 14px;
              color: $textMain;
              font-weight: 500;
              max-width: 100%;
              display: block;
              overflow: hidden;
              text-overflow: ellipsis;
              line-height: 19px;
            }
            .uik-upload__status {
              width: 100%;
              font-size: 12px;
              line-height: 15px;
              color: $textSecondary;
              max-width: 100%;
              transition: all 0.15s;
              height: 15px;
              display: flex;
              justify-content: flex-start;
              align-items: center;
              .uik-upload__status-text {
                transition: all 0.2s;
                &--completed {
                  color: $success;
                }
                &--error {
                  color: $error;
                }
              }
              .uik-upload__progress {
                width: 100%;
                transition: none;
                padding-bottom: 1px;
                .uik-progress-bar__wrapper {
                  background: rgba(darken($borderColor, 2.5%), 0.7);
                  .uik-progress-bar__progressLine {
                    transition: width 0.15s;
                  }
                }
              }
            }
          }
        }
        &:hover {
          border: solid 1px rgba($primary, 0.5);
          background: rgba($primary, 0.05);
        }
        &:active {
          border: solid 1px rgba($primary, 0.5);
          background: rgba($primary, 0.075);
        }
        .uik-upload__backdrop-progress {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 0%;
          background: rgba($borderColor, 0.5);
          z-index: 0;
          pointer-events: none;
          transition: all 0.15s;
        }
      }
      &--uploading {
        .uik-upload__file {
          .uik-upload__file-info {
            max-width: calc(100% - 27px) !important;
          }
        }
      }
      &--completed {
        .uik-upload__file {
          .uik-upload__file-icon {
            color: desaturate($success, 25%);
          }
        }
      }
      &--error {
        animation: uik-upload-file-error 0.5s ease-out;
        @keyframes uik-upload-file-error {
          0% {
            transform: none;
          }
          20% {
            transform: translateX(10px);
          }
          40% {
            transform: translateX(-10px);
          }
          60% {
            transform: translateX(5px);
          }
          80% {
            transform: translateX(-5px);
          }
          100% {
            transform: none;
          }
        }
        .uik-upload__file {
          .uik-upload__file-icon {
            color: desaturate($error, 15%);
          }
        }
      }
    }
    .uik-upload__remove-file {
      position: absolute;
      width: 32px;
      height: 32px;
      right: 15px;
      cursor: pointer;
      border-radius: 50%;
      font-size: 18px;
      background: rgba($borderColor, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      border: none;
      transition: all 0.18s;
      animation: uik-upload-remove 0.25s 0.15s backwards;
      z-index: 2;
      @keyframes uik-upload-remove {
        from {
          opacity: 0;
          transform: scale(0.5);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }
      svg {
        width: 12px;
        color: rgba($textMain, 0.3);
        position: relative;
        z-index: 1;
        transition: all 0.18s;
      }
      &::after {
        content: "";
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: rgba($error, 0.15);
        transition: all 0.18s;
        transform: scale(0.25);
        opacity: 0;
        z-index: 0;
      }
      &:hover {
        svg {
          color: $error;
        }
        &::after {
          transform: none;
          opacity: 1;
        }
      }
      &:active {
        border-color: rgba($error, 0.2);
        &::after {
          background: rgba($error, 0.2);
          opacity: 1;
          transform: none;
        }
        svg {
          color: $error;
        }
      }
    }
    .uik-upload__pond-wrapper {
      position: relative;
      display: flex;
      flex-flow: row nowrap;
      justify-content: flex-start;
      align-items: center;
      &--pond {
        .uik-upload__pond-area {
          animation: uik-upload-pond-area 0.5s;
          @keyframes uik-upload-pond-area {
            0%,
            50% {
              background: rgba($borderColor, 0.35);
              border: dashed 2px rgba($borderColorDarker, 0.8);
            }
          }
        }
      }
      &--cover {
        .uik-upload__pond-area {
          border: solid 1px rgba($borderColorDarker, 0.25);
          background: $borderColor;
          .uik-upload__pond-icons {
            transition: all 0.18s;
            .uik-upload__pond-icon {
              background: transparent;
              svg {
                --fa-secondary-opacity: 0;
              }
              &:first-child,
              &:last-child {
                display: none;
              }
            }
          }
          &:hover,
          &.uik-upload__drop {
            background: $borderColorDarker;
            border-color: $borderColorDarker;
            .uik-upload__pond-icons {
              .uik-upload__pond-icon {
                background: transparent;
                transform: none;
              }
            }
          }
          &:active {
            background: darken($borderColor, 2%);
            border-color: darken($borderColor, 2%);
          }
          &.uik-upload__drop {
            border: dashed 2px rgba($primary, 0.75);
          }
          &--file {
            &,
            &:hover,
            &:active,
            &.uik-upload__drop {
              height: 140px;
              background-repeat: no-repeat;
              background-size: cover;
              background-position: center;
              border: none;
            }
            .uik-upload__backdrop-progress {
              left: unset;
              right: 0;
              background: rgba($borderColor, 0.8);
            }
            &.uik-upload__drop {
              border: solid 1px $primary;
              .uik-upload__image-overlay {
                opacity: 1;
                svg,
                span {
                  transform: none;
                  opacity: 1;
                }
              }
            }
          }
          & + .uik-upload__remove-file {
            top: 10px;
            right: 10px;
            background: rgba(white, 0.2);
            border: none;
            transform: scale(0.8);
            opacity: 0;
            svg {
              color: white;
            }
            &::after {
              background: rgba(white, 0.2);
            }
          }
          &--error {
            .uik-upload__image-overlay {
              opacity: 1;
              background: rgba(darken($error, 20%), 0.5);
            }
          }
        }
        .uik-upload__image-overlay {
          position: absolute;
          width: 100%;
          height: 100%;
          background: rgba(darken(desaturate($primary, 60%), 30%), 0.5);
          left: 0;
          top: 0;
          display: flex;
          flex-flow: column nowrap;
          justify-content: center;
          align-items: center;
          pointer-events: none;
          opacity: 0;
          transition: all 0.18s;
          svg {
            color: rgba($borderColor, 0.8);
            font-size: 50px;
            --fa-primary-opacity: 0.85;
            --fa-secondary-opacity: 0.45;
            opacity: 0;
            transform: scale(1.25);
            transition: all 0.18s;
            & + span {
              margin-top: 5px;
            }
          }
          span {
            opacity: 0;
            font-size: 14px;
            color: white;
            transform: translateY(3px);
            transition: all 0.18s;
          }
        }
        &:hover {
          .uik-upload__image-overlay {
            opacity: 1;
            svg,
            span {
              transform: none;
              opacity: 1;
            }
          }
          .uik-upload__pond-area {
            & + .uik-upload__remove-file {
              transform: none;
              opacity: 1;
              &:active {
                transform: scale(0.9);
              }
            }
          }
        }
        &:active {
          .uik-upload__image-overlay {
            opacity: 1;
            background: rgba(darken(desaturate($primary, 60%), 30%), 0.6);
          }
          .uik-upload__pond-area--error {
            .uik-upload__image-overlay {
              opacity: 1;
              background: rgba(darken($error, 20%), 0.6);
            }
          }
        }
      }
      &--avatar {
        width: 90px;
        border-radius: 50%;
        .uik-upload__pond-area {
          border: solid 1px rgba($borderColorDarker, 0.25);
          background: $borderColor;
          width: 90px;
          height: 90px;
          padding: 0;
          border-radius: 50%;
          .uik-upload__pond-icons {
            transition: all 0.18s;
            transform: translateY(-1px);
            .uik-upload__pond-icon {
              font-size: 36px;
              background: transparent;
              svg {
                --fa-secondary-opacity: 0.5;
              }
              &:first-child,
              &:last-child {
                display: none;
              }
            }
          }
          &:hover,
          &.uik-upload__drop {
            background: $borderColorDarker;
            border-color: $borderColorDarker;
            .uik-upload__pond-icons {
              .uik-upload__pond-icon {
                background: transparent;
                transform: none;
                svg {
                  --fa-secondary-color: #{$primary};
                }
              }
            }
          }
          &:active {
            background: darken($borderColor, 2%);
            border-color: darken($borderColor, 2%);
          }
          &.uik-upload__drop {
            border: dashed 2px rgba($primary, 0.75);
          }
          &--file {
            border: none;
            &,
            &:hover,
            &:active,
            &.uik-upload__drop {
              height: 90px;
              background-repeat: no-repeat;
              background-size: cover;
              background-position: center;
            }
            .uik-upload__backdrop-progress {
              width: 100%;
              left: 0;
              top: 0;
              bottom: unset;
              right: unset;
              background: rgba($borderColor, 0.6);
            }
            &.uik-upload__drop {
              border: solid 1px $primary;
              .uik-upload__image-overlay {
                opacity: 1;
                svg,
                span {
                  transform: none;
                  opacity: 1;
                }
              }
            }
          }
          & + .uik-upload__remove-file {
            top: 0px;
            right: 0px;
            background: rgba($borderColorDarker, 0.85);
            border: none;
            width: 28px;
            height: 28px;
            transform: scale(0.25);
            opacity: 0;
            svg {
              color: $textBase;
            }
            &::after {
              background: rgba($error, 0.2);
            }
          }
          &--error {
            .uik-upload__image-overlay {
              opacity: 1;
              background: rgba(darken($error, 20%), 0.5);
            }
          }
        }
        .uik-upload__image-overlay {
          position: absolute;
          width: 100%;
          height: 100%;
          background: rgba(darken(desaturate($primary, 60%), 30%), 0.6);
          left: 0;
          top: 0;
          display: flex;
          flex-flow: column nowrap;
          justify-content: center;
          align-items: center;
          pointer-events: none;
          opacity: 0;
          transition: all 0.18s;
          svg {
            color: rgba($borderColor, 0.8);
            font-size: 26px;
            --fa-primary-opacity: 0.9;
            --fa-secondary-opacity: 0.75;
            opacity: 0;
            transform: scale(1.4);
            transition: all 0.18s;
            & + span {
              margin-top: 5px;
            }
          }
          span {
            opacity: 0;
            font-size: 12px;
            color: white;
            transform: translateY(2px);
            transition: all 0.18s;
          }
        }
        &:hover {
          .uik-upload__image-overlay {
            opacity: 1;
            svg,
            span {
              transform: none;
              opacity: 1;
            }
          }
          .uik-upload__pond-area {
            & + .uik-upload__remove-file {
              transform: scale(0.85);
              opacity: 1;
              &:hover {
                transform: none;
                svg {
                  color: $error;
                }
              }
              &:active {
                transform: scale(0.85);
              }
            }
          }
        }
        &:active {
          .uik-upload__image-overlay {
            opacity: 1;
            background: rgba(darken(desaturate($primary, 60%), 30%), 0.7);
          }
          .uik-upload__pond-area--error {
            .uik-upload__image-overlay {
              opacity: 1;
              background: rgba(darken($error, 20%), 0.6);
            }
          }
        }
      }
    }
    &--avatar {
      width: auto;
    }
  }
  .uik-upload__overlay {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: transparent;
    z-index: 100;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    transition: all 0.18s;
    pointer-events: none;
    .uik-upload__overlay-content {
      padding: 25px;
      position: relative;
      z-index: 1;
      display: flex;
      flex-flow: column nowrap;
      justify-content: center;
      align-items: center;
      .uik-upload__overlay-icons {
        position: relative;
        display: flex;
        flex-flow: row nowrap;
        justify-content: center;
        align-items: center;
        opacity: 0;
        transition: all 0.18s;
        .uik-upload__overlay-icon {
          font-size: 80px;
          display: flex;
          flex-flow: row nowrap;
          justify-content: center;
          align-items: center;
          transition: all 0.18s;
          position: relative;
          z-index: 2;
          opacity: 1;
          svg {
            transition: all 0.18s;
            --fa-primary-opacity: 1;
            --fa-secondary-opacity: 1;
            color: desaturate(lighten($primary, 17%), 12%);
            .fa-primary {
              fill: white;
            }
          }
          animation: uik-upload-overlay-icon 1.3s ease-in-out infinite;
          @keyframes uik-upload-overlay-icon {
            0%,
            100% {
              transform: none;
            }
            50% {
              transform: translateY(-20px);
            }
          }
          &:first-child,
          &:last-child {
            animation-delay: 0.1s;
            background: transparent;
            position: absolute;
            opacity: 0;
            z-index: 1;
            svg {
              color: desaturate(lighten($primary, 12%), 10%);
              .fa-primary {
                fill: white;
              }
            }
          }
        }
        & + .uik-upload__overlay-text {
          margin-top: 15px;
        }
      }
      .uik-upload__overlay-text {
        transition: all 0.18s;
        font-size: 18px;
        color: white;
        transform: translateY(20px);
        opacity: 0;
      }
    }
    &.uik-upload__drop {
      background: rgba(black, 0.6);
      .uik-upload__overlay-content {
        .uik-upload__overlay-icons {
          opacity: 1;
          .uik-upload__overlay-icon {
            opacity: 1;
            svg {
              transform: scale(1.2) translateY(-15px);
            }
            &:first-child,
            &:last-child {
              opacity: 0.9;
            }
            &:first-child {
              svg {
                transform: scale(0.9) translateY(-15px) translateX(-38px)
                  rotate(-15deg);
              }
            }
            &:last-child {
              svg {
                transform: scale(0.9) translateY(-15px) translateX(38px)
                  rotate(15deg);
              }
            }
          }
        }
        .uik-upload__overlay-text {
          transform: none;
          opacity: 1;
        }
      }
    }
  }
}
</style>
